import { useEffect, useMemo, useState } from 'react';
import PageContainer from '../components/PageContainer';
import TimeRangeSelector, { ranges } from '../components/TimeRangeSelector';
import financeData from '../finance_data.json';
import FaultShutdownsChart from '../components/FaultShutdownsChart';
import IdleCostTotalsChart from '../components/IdleCostTotalsChart';
import EquipmentUtilizationChart from '../components/EquipmentUtilizationChart';
import DowntimeGaugeChart from '../components/DowntimeGaugeChart';
import DowntimeVsOperationalChart from '../components/DowntimeVsOperationalChart';

const panelClasses =
  'flex flex-col min-h-0 overflow-hidden rounded-3xl border border-white/10 bg-white/70 p-4 sm:p-5 text-slate-900 shadow-xl shadow-slate-900/10 backdrop-blur-xl dark:border-white/5 dark:bg-slate-900/60 dark:text-white dark:shadow-black/20';

const currencyFormatter = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: financeData.currency,
  maximumFractionDigits: 0,
});

function getRangeKey(range) {
  if (range.unit === 'hour' && range.value === 1) return 'last_1h';
  if (range.unit === 'hour' && range.value === 24) return 'last_24h';
  if (range.unit === 'day' && range.value === 7) return 'last_7d';
  return 'last_24h';
}

export default function Finance() {
  const [range, setRange] = useState(ranges[1]);
  const [compactLayout, setCompactLayout] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 1400 : false
  );
  const rangeData = useMemo(
    () =>
      financeData.time_ranges[getRangeKey(range)] || {
        kpis: {
          idle_cost: 0,
          shutdown_cost: 0,
          production_lost_cost: 0,
          total_cost: 0,
        },
        fault_shutdowns: [],
        idle_cost_totals: [],
        utilization: [],
      },
    [range]
  );

  const summaryCards = [
    {
      label: 'Idle Cost',
      value: currencyFormatter.format(rangeData.kpis.idle_cost),
      description: 'Stood still but still spending',
      gradient: 'from-sky-400/90 to-blue-500/70',
    },
    {
      label: 'Shutdown',
      value: currencyFormatter.format(rangeData.kpis.shutdown_cost),
      description: 'Planned and unplanned stoppages',
      gradient: 'from-rose-400/90 to-amber-400/70',
    },
    {
      label: 'Production Lost',
      value: currencyFormatter.format(rangeData.kpis.production_lost_cost),
      description: 'Revenue at risk',
      gradient: 'from-purple-400/90 to-indigo-500/70',
    },
    {
      label: 'Total Cost',
      value: currencyFormatter.format(rangeData.kpis.total_cost),
      description: 'Combined operational impact',
      gradient: 'from-emerald-400/90 to-teal-500/70',
    },
  ];

  useEffect(() => {
    const onResize = () => setCompactLayout(window.innerWidth <= 1400);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <PageContainer className="relative overflow-hidden bg-slate-950 text-slate-100">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.18),transparent_55%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.16),transparent_55%)]" />
      <div className="relative z-10 flex flex-1 flex-col gap-8">
        <header className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="space-y-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-200">
                Financial Pulseboard
              </span>
              <div>
                <h1 className="text-2xl font-semibold leading-tight text-white sm:text-3xl md:text-4xl">Finance</h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
                  Holistic visibility into cost drivers, utilisation performance, and downtime impact across your operation.
                </p>
              </div>
            </div>
            <TimeRangeSelector value={range} onChange={setRange} />
          </div>

          <div className={`grid gap-4 sm:grid-cols-2 ${compactLayout ? 'xl:grid-cols-3' : 'xl:grid-cols-4 2xl:grid-cols-4'}`}>
            {summaryCards.map(({ label, value, description, gradient }) => (
              <div
                key={label}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/10 p-5 transition hover:border-white/30 hover:bg-white/20 dark:border-white/5 dark:bg-white/5"
              >
                <div className={`absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br opacity-80 blur-2xl transition group-hover:opacity-100 ${gradient}`} />
                <div className="relative z-10 flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-200">{label}</span>
                  <span className="text-3xl font-semibold text-white">{value}</span>
                  <span className="text-xs text-slate-300">{description}</span>
                </div>
              </div>
            ))}
          </div>
        </header>

        <div className="flex flex-1 min-h-0 flex-col gap-6">
          <div className={`grid gap-6 ${compactLayout ? 'md:grid-cols-2' : 'xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1fr)]'}`}>
            <section className={`${panelClasses} min-h-[240px]`}>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white sm:text-lg">Downtime</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">24h outlook</span>
              </div>
              <div className="mt-4 h-56 flex-1 sm:h-64">
                <DowntimeGaugeChart />
              </div>
            </section>
            <section className={`${panelClasses} min-h-[280px]`}>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white sm:text-lg">Fault Shutdowns</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">By asset</span>
              </div>
              <div className="mt-4 h-60 flex-1 sm:h-64">
                <FaultShutdownsChart data={rangeData.fault_shutdowns} />
              </div>
            </section>
            <section className={`${panelClasses} min-h-[280px]`}>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white sm:text-lg">Idle Cost Totals</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">Cost per unit</span>
              </div>
              <div className="mt-4 h-60 flex-1 sm:h-64">
                <IdleCostTotalsChart data={rangeData.idle_cost_totals} />
              </div>
            </section>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <section className={`${panelClasses} min-h-[320px]`}>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white sm:text-lg">Downtime vs. Operational Time</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">Latest snapshot</span>
              </div>
              <div className="mt-4 h-60 flex-1 sm:h-64">
                <DowntimeVsOperationalChart />
              </div>
            </section>
            <section className={`${panelClasses} min-h-[320px]`}>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white sm:text-lg">Equipment Utilisation Efficiency</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">Stacked performance</span>
              </div>
              <div className="mt-4 h-64 flex-1 sm:h-72 md:h-80">
                <EquipmentUtilizationChart data={rangeData.utilization} />
              </div>
            </section>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
